import { Signal } from "@suse-o11y/signal";
import React from "react";
import { useMeasure } from "react-use";

import { IconEnlarge } from "~/components/icon";
import { SkeletonBox } from "~/components/skeleton/box";
import { Tooltip } from "~/components/tooltip";
import { TooltipContent } from "~/components/tooltip/content";
import { TooltipTrigger } from "~/components/tooltip/trigger";
import { ZIndexBoundary } from "~/components/z-index-boundary";
import type { MetricQuery } from "~/models/metric-query";
import type { Time } from "~/tools/date-time/time";
import { mergeRefs } from "~/tools/merge-refs";
import { prop } from "~/tools/prop";
import { Template } from "~/tools/template";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import type { PinnedMetricComponent } from "../../bottom_content/view/panes/east/pinned-items/_metric";
import { normalizePinnedMetricComponent } from "../../bottom_content/view/panes/east/pinned-items/PinnedItem";
import { PinningButton } from "../../bottom_content/view/panes/east/pinned-items/PinningButton";
import { DashboardChartPluginSpec, DashboardWidgetSpec } from "../../common/dashboard-spec";
import { TimeSeriesQuery } from "../../common/dashboard-spec/_time-series-query";
import { queryUIConfig } from "../../queries";
import { useQuery } from "../../queries/react-query";
import { useComponentBoundary } from "../_component-boundary";
import { CtxMenuItem } from "../react/CtxMenu/CtxMenuItem";
import { CtxPopoverMenu } from "../react/CtxMenu/CtxPopoverMenu";
import { EmptyChart } from "../react/chart/chart-states/EmptyChart";
import { ErrorChart } from "../react/chart/chart-states/ErrorChart";
import { useExplorationDrawerState } from "../react/drawer/use-exploration-drawer-state";

import { fromMetricQuery as widgetQueryFromMetricQuery } from "./query/spec";
import { WidgetLayout, type WidgetLayoutProps } from "./_widget-layout";
import type { Spec } from "./spec";
import { type UseQueryTimeSeriesParams, useQueryTimeSeries } from "./use-query-time-series";

import { Chart, type ChartProps } from ".";

export interface ChartWidgetProps
	extends Pick<WidgetLayoutProps, "className" | "title" | "description">,
		Pick<UseQueryTimeSeriesParams, "namespace" | "retry" | "batch">,
		Pick<ChartProps, "onTimeRangeChange"> {
	queries: ReadonlyArray<MetricQuery>;
	chart: Spec;
	timestamp: undefined | Time;
	timeRange: TimeRange;
}

export const ChartWidget = React.forwardRef<React.ComponentRef<"div">, ChartWidgetProps>(
	(
		{ className, title, description, namespace, retry, batch, queries, chart, timestamp, timeRange, onTimeRangeChange },
		ref,
	) => {
		const [measureRef, { width }] = useMeasure<HTMLDivElement>();

		const explorationDrawer = useExplorationDrawerState();
		const component = normalizePinnedMetricComponent(useComponentBoundary());

		const result = useQueryTimeSeries({
			namespace,
			retry,
			batch,
			width: chart.kind === "StatChart" || chart.kind === "GaugeChart" ? undefined : width,
			timeRange,
			queries,
		});

		const onOpenMetricsPreview = () => {
			explorationDrawer.openMetrics({
				metricQueries: queries,
				timeRange: Signal(timeRange),
				title,
				chart,
				componentIdOrIdentifier: component?.identifier,
				timestamp,
			});
		};

		const onAddToDashboard = () => {
			const widget = DashboardWidgetSpec.of({
				name: Template(title ?? ""),
				description: description == null ? undefined : Template(description),
				plugin: {
					links: [],
					queries: queries.map((query) => TimeSeriesQuery.fromQuerySpec(widgetQueryFromMetricQuery(query))),
					...DashboardChartPluginSpec.fromChartSpec(chart),
				},
			});

			explorationDrawer.openAddToDashboard([widget]);
		};

		return (
			<WidgetLayout
				className={className}
				description={description}
				ref={mergeRefs([ref, measureRef])}
				title={title}
				toolbar={
					<Toolbar
						chart={chart}
						component={component}
						description={description}
						onAddToDashboard={onAddToDashboard}
						onOpenMetricsPreview={onOpenMetricsPreview}
						queries={queries}
						title={title}
					/>
				}
			>
				{result.match({
					idle: () => <EmptyChart size="lg" />,

					loading: () => <SkeletonBox className="h-55" />,

					failure: ({ error }) => <ErrorChart error={error} size="lg" />,

					success: ({ value: { timeSeries } }) => {
						if (timeSeries.length === 0) {
							return <EmptyChart size="lg" />;
						}

						return (
							<Chart
								canvasHeight={200}
								fallback={<SkeletonBox className="h-55" />}
								onShowAll={onOpenMetricsPreview}
								onTimeRangeChange={onTimeRangeChange}
								spec={chart}
								timeRange={timeRange}
								timeSeries={timeSeries}
							/>
						);
					},
				})}
			</WidgetLayout>
		);
	},
);

interface ToolbarProps extends Pick<ChartWidgetProps, "title" | "description" | "queries" | "chart"> {
	component: undefined | PinnedMetricComponent;
	onOpenMetricsPreview: VoidFunction;
	onAddToDashboard: VoidFunction;
}

const Toolbar: React.FC<ToolbarProps> = ({
	title,
	description,
	queries,
	chart,
	component,
	onOpenMetricsPreview,
	onAddToDashboard,
}) => {
	const { data: hasDashboardsFeature } = useQuery({
		...queryUIConfig,
		select: prop("webUIConfig", "featureFlags", "dashboards"),
	});

	return (
		<div className="flex gap-4">
			{component != null && (
				<PinningButton item={{ name: title, description, metricQueries: queries, component, chart }} onlyIcon />
			)}

			<Tooltip>
				<TooltipTrigger>
					<button
						className="flex cursor-pointer items-center text-grey-800 hover:text-primary-500"
						onClick={onOpenMetricsPreview}
						type="button"
					>
						<IconEnlarge className="pointer-events-none" />
					</button>
				</TooltipTrigger>

				<TooltipContent>Open in metric preview</TooltipContent>
			</Tooltip>

			{hasDashboardsFeature && (
				<ZIndexBoundary zIndex={(closestZIndex = 0) => closestZIndex + 50}>
					{(zIndex) => (
						<CtxPopoverMenu ellipsisClassName="m-0" zIndex={zIndex}>
							<CtxMenuItem label="Add to dashboard" onClick={onAddToDashboard} />
						</CtxPopoverMenu>
					)}
				</ZIndexBoundary>
			)}
		</div>
	);
};
